// Ordering and filtering the recipe catalogue.
//
// Outside the component because vitest here has no React renderer: which row is
// at the top is exactly the kind of thing that is wrong quietly, and it is the
// first thing a reader of this page believes.
import type { RecipeRow } from "../api/types";

/** Which reading of the cost the page is showing: every material at its own ask,
 *  or the cheaper of buying and crafting at every node. */
export type CostMode = "buy" | "craft";
export type RecipeSort = "margin" | "item" | "grade" | "cost" | "level";

export function costOf(row: RecipeRow, mode: CostMode): number | null {
  return mode === "buy" ? row.cost_buy : row.cost_craft;
}

/** The margin under this cost reading. Both are struck server-side against the
 *  crafted item's highest bid, so the mode chooses which cost is subtracted,
 *  never which price it is subtracted from. */
export function marginOf(row: RecipeRow, mode: CostMode): number | null {
  return mode === "buy" ? row.margin_buy : row.margin_craft;
}

export function pricedOf(row: RecipeRow, mode: CostMode): number {
  return mode === "buy" ? row.priced_buy : row.priced_craft;
}

/** The game's order, which is not alphabetical: no grade, then D through S. An
 *  unknown grade sorts after every known one rather than crashing the compare. */
const GRADES = ["NG", "D", "C", "B", "A", "S"];

function gradeRank(grade: string): number {
  const rank = GRADES.indexOf(grade.toUpperCase());
  return rank === -1 ? GRADES.length : rank;
}

/** Case-insensitive, because the names came from CITEXT columns. Ordering by
 *  byte value would put "zephyr" before "Ashenvale" and disagree with every
 *  list the database produces. */
function byText(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

/** A row whose cost could not be completed sorts last under every order.
 *
 *  This is the rule the page's honesty rests on. An unknown is not a zero, and
 *  a row missing its most expensive material would otherwise open the page as
 *  the best margin in the catalogue. */
function incomplete(row: RecipeRow, mode: CostMode): boolean {
  return costOf(row, mode) === null;
}

export function sortRecipes(rows: RecipeRow[], sort: RecipeSort, mode: CostMode): RecipeRow[] {
  // A copy: Array.prototype.sort is in place and these rows come straight out
  // of react-query's cache. Sort is stable in ES2019+, which is what keeps
  // equal rows from swapping between renders.
  return [...rows].sort((a, b) => {
    const aOut = incomplete(a, mode);
    const bOut = incomplete(b, mode);
    if (aOut !== bOut) return aOut ? 1 : -1;

    if (sort === "margin") {
      // Best first, and a row with a cost but nobody bidding on the crafted
      // item is not a zero margin — it goes after every row that has one.
      const am = marginOf(a, mode);
      const bm = marginOf(b, mode);
      if (am === null || bm === null) {
        if (am !== bm) return am === null ? 1 : -1;
      } else if (am !== bm) {
        return bm - am;
      }
    }
    if (sort === "cost") {
      const ac = costOf(a, mode) ?? 0;
      const bc = costOf(b, mode) ?? 0;
      if (ac !== bc) return ac - bc;
    }
    if (sort === "grade") {
      const rank = gradeRank(a.grade) - gradeRank(b.grade);
      if (rank !== 0) return rank;
      if (a.craft_level !== b.craft_level) return a.craft_level - b.craft_level;
    }
    if (sort === "level" && a.craft_level !== b.craft_level) {
      return a.craft_level - b.craft_level;
    }
    return byText(a.craft_item, b.craft_item);
  });
}

/** Matches the crafted item and the recipe's own name, because a reader hunting
 *  "arrow" means either. */
export function filterRecipes(rows: RecipeRow[], needle: string): RecipeRow[] {
  const term = needle.trim().toLowerCase();
  if (!term) return rows;
  return rows.filter(
    (row) =>
      row.craft_item.toLowerCase().includes(term) || row.name.toLowerCase().includes(term),
  );
}
